import React from 'react';
import { FragmentType, graphql, useFragment } from '@frontend/gql/social';
import { Card } from '@frontend/shadcn/components/ui/card';
import { MetricChangeIndicator } from '@frontend/applications/Social/components/metric-change-indicator';
import {
  HighlightCardHeader,
  HighlightCardHeaderRow,
  HighlightCardTitle,
  HighlightCardContent,
  HighlightCardContentRow,
  HighlightCardContentColumn,
  HighlightCardStatRow,
  HighlightCardStat,
  HighlightCardStatLabel,
} from '@frontend/applications/Social/components/highlight-card';
import { DateType } from '@frontend/applications/Social/Analytics/types';
import { CartShoppingIcon } from '@revfluence/fresh-icons/solid/esm';
import { calculateSpendMetrics } from './utils/calculateSpendMetrics';

const salesGeneratedCardFragment = graphql(`
  fragment SalesGeneratedCard_MetaSumInsights on MetaSumInsights {
    totalPurchase {
      amount
      currency
    }
  }
`);

type SalesGeneratedCardProps = {
  currentInterval: FragmentType<typeof salesGeneratedCardFragment> | null | undefined;
  previousInterval: FragmentType<typeof salesGeneratedCardFragment> | null | undefined;
  currentIntervalNotAspireSourced: FragmentType<typeof salesGeneratedCardFragment> | null | undefined;
  previousIntervalNotAspireSourced: FragmentType<typeof salesGeneratedCardFragment> | null | undefined;
  showNonAttributedData?: boolean;
  startDate?: string;
  endDate?: string;
  dateType?: DateType;
};

const SalesGeneratedCard = ({
  currentInterval,
  previousInterval,
  currentIntervalNotAspireSourced,
  previousIntervalNotAspireSourced,
  showNonAttributedData = false,
  startDate,
  endDate,
  dateType,
}: SalesGeneratedCardProps) => {
  const currentIntervalData = useFragment(salesGeneratedCardFragment, currentInterval);
  const previousIntervalData = useFragment(salesGeneratedCardFragment, previousInterval);
  const currentIntervalNotAspireSourcedData = useFragment(salesGeneratedCardFragment, currentIntervalNotAspireSourced);
  const previousIntervalNotAspireSourcedData = useFragment(
    salesGeneratedCardFragment,
    previousIntervalNotAspireSourced,
  );

  const { formattedCurrentInterval, formattedCurrentIntervalNotAspireSourced } = calculateSpendMetrics({
    currentInterval: currentIntervalData,
    previousInterval: previousIntervalData,
    currentIntervalNotAspireSourced: currentIntervalNotAspireSourcedData,
  });

  return (
    <Card>
      <HighlightCardHeader className="bg-[#2F7772]" role="banner">
        <HighlightCardHeaderRow>
          <HighlightCardTitle>
            <CartShoppingIcon className="mr-2 text-[1.5rem]" aria-hidden="true" />
            <span className="text-base">Sales Generated from Ads</span>
          </HighlightCardTitle>
        </HighlightCardHeaderRow>
      </HighlightCardHeader>
      <HighlightCardContent>
        <HighlightCardContentRow>
          <HighlightCardContentColumn className={showNonAttributedData ? '' : 'col-span-full'}>
            <HighlightCardStatRow>
              <HighlightCardStat
                className="text-3xl leading-none"
                role="status"
                aria-label={`Sales generated attributed to Aspire: ${formattedCurrentInterval}`}
                data-dd-action-name="sales-aspire-stat"
              >
                {formattedCurrentInterval}
              </HighlightCardStat>
            </HighlightCardStatRow>
            <HighlightCardStatLabel>Attributed to Aspire</HighlightCardStatLabel>
            <MetricChangeIndicator
              current={currentIntervalData?.totalPurchase?.amount || 0}
              previous={previousIntervalData?.totalPurchase?.amount || 0}
              startDate={startDate}
              endDate={endDate}
              dateType={dateType}
            />
          </HighlightCardContentColumn>
          {showNonAttributedData && (
            <HighlightCardContentColumn className="xs:items-start sm:items-end md:items-start xl:items-end">
              <HighlightCardStatRow>
                <HighlightCardStat
                  className="text-3xl leading-none"
                  role="status"
                  aria-label={`Sales generated not attributed to Aspire: ${formattedCurrentIntervalNotAspireSourced}`}
                  data-dd-action-name="sales-not-aspire-stat"
                >
                  {formattedCurrentIntervalNotAspireSourced}
                </HighlightCardStat>
              </HighlightCardStatRow>
              <HighlightCardStatLabel>Not Attributed to Aspire</HighlightCardStatLabel>
              <MetricChangeIndicator
                current={currentIntervalNotAspireSourcedData?.totalPurchase?.amount || 0}
                previous={previousIntervalNotAspireSourcedData?.totalPurchase?.amount || 0}
                startDate={startDate}
                endDate={endDate}
                dateType={dateType}
              />
            </HighlightCardContentColumn>
          )}
        </HighlightCardContentRow>
      </HighlightCardContent>
    </Card>
  );
};

export default SalesGeneratedCard;
